import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Stack,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatAvailabilityDate } from '../../utils/formatters';
import { rentalDetailPath } from '../../utils/listingPaths';

/** 价格强调色：深棕，与首页卡片一致 */
const listingEmphasisText = '#3d2914';

function toNumberPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** 列表卡片：整租/分间/分床的最低月租（仅含当前仍可选的价格），否则用房源顶层 price */
function getRentalStartingPrice(rental) {
  if (!rental) return null;
  const prices = [];
  const type = rental.rental_type;

  const pushIfListed = (item) => {
    if (item?.isAvailable === false) return;
    const p = toNumberPrice(item?.price);
    if (p != null) prices.push(p);
  };

  if (type === 'entire' && Array.isArray(rental.units)) {
    rental.units.forEach(pushIfListed);
  } else if (type === 'room' && Array.isArray(rental.rooms)) {
    rental.rooms.forEach(pushIfListed);
  } else if (type === 'bed' && Array.isArray(rental.beds)) {
    rental.beds.forEach(pushIfListed);
  }

  if (prices.length > 0) return Math.min(...prices);
  return toNumberPrice(rental.price);
}

function RentalCard({
  rental,
  isFavorited = false,
  onFavoriteClick,
  selected = false,
  hovered = false,
  onListingClick,
  onMouseEnter,
  onMouseLeave,
}) {
  const navigate = useNavigate();

  const img =
    rental.images?.[0] ||
    'https://source.unsplash.com/random/800x600/?apartment';
  const address = [
    rental.address_line1,
    rental.address_line2,
    rental.city,
    rental.state,
    rental.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
  const startPrice = getRentalStartingPrice(rental);
  const priceLabel =
    startPrice != null ? `$${Math.round(startPrice)}/mo` : '—';
  const amenityChip =
    Array.isArray(rental.amenities) && rental.amenities.length > 0
      ? rental.amenities[0]
      : null;

  const goDetail = useCallback(() => {
    if (typeof onListingClick === 'function') {
      onListingClick(rental.id);
    }
    navigate(rentalDetailPath(rental));
  }, [navigate, onListingClick, rental]);

  const handleFavoriteButtonClick = (e) => {
    e.stopPropagation();
    if (typeof onFavoriteClick === 'function') {
      onFavoriteClick(rental.id, e);
    }
  };

  return (
    <Card
      elevation={selected ? 4 : 2}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s, background-color 0.2s',
        border: selected ? '2px solid' : '2px solid transparent',
        borderColor: selected ? 'primary.main' : 'transparent',
        boxShadow: selected ? 8 : undefined,
        bgcolor: hovered && !selected ? 'grey.50' : 'background.paper',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={goDetail}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia component="img" height="200" image={img} alt={rental.title} />
        {amenityChip && (
          <Chip
            label={amenityChip}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              bgcolor: 'rgba(255,255,255,0.95)',
              fontWeight: 600,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Chip
          label="Available"
          size="small"
          color="success"
          sx={{ position: 'absolute', bottom: 10, left: 10, fontWeight: 600 }}
          onClick={(e) => e.stopPropagation()}
        />
        {typeof onFavoriteClick === 'function' && (
          <IconButton
            type="button"
            size="small"
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.92)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
            }}
            onClick={handleFavoriteButtonClick}
          >
            {isFavorited ? (
              <FavoriteIcon fontSize="small" sx={{ color: 'error.main' }} />
            ) : (
              <FavoriteBorderIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </IconButton>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {rental.title}
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: listingEmphasisText, flexShrink: 0 }}
          >
            {priceLabel}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {address || 'Los Angeles, CA'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2, color: 'text.secondary' }}>
          <CalendarTodayIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            Available {formatAvailabilityDate(rental.available_date)}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 'auto',
            bgcolor: '#1a1a2e',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.25,
            '&:hover': { bgcolor: '#16213e' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            goDetail();
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

export default RentalCard;
